// Which batches the queue shows by default, and how it pages them.
//
// Pure functions, separate from the view, so the rules are testable without
// rendering — the same reason batchGrouping is separate.

import type { AuditBatch } from "../types";

/** FA's BATCH_STATUS_CODE for a closed batch.
 *
 *  Established from FirstClaim's own source (CodingDAO, RefundBatchAction,
 *  BalanceBillerConstants): 'C' closed, 'P' pending, 'D' deleted, 'F' frozen.
 *  Measured over 277 production batches it is 'C' 87.7% / 'P' 12.3%. */
const FA_STATUS_CLOSED = "c";

/**
 * The batches worth reviewing.
 *
 * A batch qualifies when FA's coding is COMPLETE — every active episode coded —
 * or when the batch is CLOSED, whether or not the coding finished. A closed
 * batch that is not fully coded still belongs in the queue; the card shows its
 * coded/uncoded/inactive split rather than hiding the shortfall.
 *
 * A batch with no FA status at all is kept. Those are ingest-day buckets, which
 * have no FA batch record by construction — dropping them would empty the queue
 * of exactly the PDF-only cases it exists to review.
 */
export function reviewableBatches(batches: readonly AuditBatch[]): AuditBatch[] {
  return batches.filter(isReviewable);
}

function isReviewable(b: AuditBatch): boolean {
  // An ingest-day bucket is a DEAD END, not a batch. It is synthesised for cases
  // that arrived by PDF before FA's coder leg, so it has no FA batch record and
  // is never assigned to a queue — which means IsAssignedTo denies every case in
  // it and a reviewer who opens one sees nothing. Listing an unopenable batch is
  // worse than omitting it.
  //
  // Measured in production 2026-08-21: 0 of 6,241 assigned batches were intake
  // buckets, and 0 of 298 recent cases lacked an FA batch id. The condition that
  // creates them — PDF before the FA leg — no longer occurs; what remains is
  // historical residue.
  //
  // Hidden from the DEFAULT view only. The queue's "show all batches" toggle
  // still reveals them, because a bucket holding real cases must stay reachable
  // if that condition ever returns.
  if (isIngestDayBucket(b)) return false;
  if (b.codingComplete) return true;
  const status = b.faStatus?.trim().toLowerCase();
  // Absent status on a REAL FA batch: keep it rather than guess.
  if (!status) return true;
  return status === FA_STATUS_CLOSED;
}

/**
 * A synthetic ingest-day bucket rather than one of FA's batches.
 *
 * The `intake-YYYY-MM-DD` shape is the server's documented form for this
 * fallback (see evalreview.proto and caseBatchID), not a guess about ids.
 */
export function isIngestDayBucket(b: AuditBatch): boolean {
  return b.id.startsWith('intake-');
}

/**
 * Whether a batch's coded/uncoded/inactive split is worth showing.
 *
 * Only when it says something: a fully-coded batch's split is "all of them",
 * which is already what the state pill says. Suppressing it there keeps the
 * count meaningful where it does appear.
 */
export function hasCodingShortfall(b: AuditBatch): boolean {
  return !b.codingComplete && (b.uncodedEpisodes > 0 || b.inactiveEpisodes > 0);
}

/**
 * Whether FA has any batch-level episode figures for this batch at all.
 *
 * Distinguishes "FA says zero" from "FA has never heard of this batch". The
 * second is an ingest-day bucket, which has no FA batch record by construction,
 * and showing it a row of zeros reads as a batch where nothing has been done.
 */
export function hasFACounts(b: AuditBatch): boolean {
  return (
    b.activeEpisodes > 0 ||
    b.codedEpisodes > 0 ||
    b.inactiveEpisodes > 0 ||
    b.declaredCount > 0
  );
}

/**
 * FA's batch status codes, from FirstClaim's own source (CodingDAA,
 * RefundBatchAction, BalanceBillerConstants).
 *
 * Only 'C' and 'P' have been observed in production (87.7% / 12.3% over 277
 * batches). 'D' and 'F' are carried because FA can emit them and a filter that
 * silently drops an unrecognised status hides batches rather than showing them.
 */
export const FA_STATUS = {
  closed: "c",
  pending: "p",
  deleted: "d",
  frozen: "f",
} as const;

/** The coding-completeness axis: has FA's human coding finished? */
export type CodingFilter = "any" | "complete" | "incomplete";

/** The FA batch-status axis: is the batch closed, or still open? */
export type StatusFilter = "any" | "closed" | "open" | "unknown";

/** Whether the batch sits in a reviewer queue. */
export type AssignmentFilter = "any" | "assigned" | "unassigned";

/**
 * An inclusive date range over the batch's own date, as epoch milliseconds.
 *
 * Bounds are epoch ms rather than strings for the same reason `dosSort` exists:
 * `dos` is locale-formatted, and comparing "8/7/2026" to "8/18/2026" as text
 * puts the 7th after the 18th. Null means unbounded on that side.
 */
export interface DateRange {
  fromMs: number | null;
  toMs: number | null;
}

export const ANY_DATES: DateRange = { fromMs: null, toMs: null };

/** All the filters the batch list applies beyond search and reviewability. */
export interface BatchFilters {
  range: DateRange;
  coding: CodingFilter;
  status: StatusFilter;
  assignment: AssignmentFilter;
}

export const DEFAULT_BATCH_FILTERS: BatchFilters = {
  range: ANY_DATES,
  coding: "any",
  status: "any",
  assignment: "any",
};

/**
 * Whether a batch's date falls in the range.
 *
 * A batch with no usable date (`dosSort` 0) is EXCLUDED once either bound is
 * set, and kept when the range is open. Including undated batches in a bounded
 * range would put rows in a result the reviewer explicitly narrowed, and they
 * would have no visible reason to be there.
 */
export function withinDateRange(b: AuditBatch, range: DateRange): boolean {
  if (range.fromMs === null && range.toMs === null) return true;
  if (!b.dosSort) return false;
  if (range.fromMs !== null && b.dosSort < range.fromMs) return false;
  if (range.toMs !== null && b.dosSort > range.toMs) return false;
  return true;
}

/** Whether the batch matches the coding-completeness filter. */
export function matchesCoding(b: AuditBatch, f: CodingFilter): boolean {
  if (f === "any") return true;
  return f === "complete" ? b.codingComplete : !b.codingComplete;
}

/**
 * Whether the batch matches the FA status filter.
 *
 * "open" is everything that is not closed AND not absent — deliberately not
 * "anything that isn't 'c'", because an ingest-day bucket has no FA status at
 * all and is not an open batch, it is a batch FA has never heard of. That is
 * what "unknown" selects.
 */
export function matchesStatus(b: AuditBatch, f: StatusFilter): boolean {
  if (f === "any") return true;
  const s = b.faStatus?.trim().toLowerCase() ?? "";
  if (f === "unknown") return s === "";
  if (s === "") return false;
  return f === "closed" ? s === FA_STATUS.closed : s !== FA_STATUS.closed;
}

/** Whether the batch matches the queue-assignment filter. */
export function matchesAssignment(
  b: AuditBatch,
  f: AssignmentFilter,
  assignedIds: ReadonlySet<string>,
): boolean {
  if (f === "any") return true;
  const assigned = assignedIds.has(b.id);
  return f === "assigned" ? assigned : !assigned;
}

/**
 * Apply every non-search filter.
 *
 * Search stays at the call site and runs AFTER this, so a query can never
 * surface a batch the active filters exclude — the same rule reviewability
 * already follows.
 */
export function applyBatchFilters(
  batches: readonly AuditBatch[],
  filters: BatchFilters,
  assignedIds: ReadonlySet<string>,
): AuditBatch[] {
  return batches.filter(
    (b) =>
      withinDateRange(b, filters.range) &&
      matchesCoding(b, filters.coding) &&
      matchesStatus(b, filters.status) &&
      matchesAssignment(b, filters.assignment, assignedIds),
  );
}

/** Whether any filter is narrowing the list — drives the "clear" affordance and
 *  the empty-state copy, which must distinguish "no batches" from "none match". */
export function hasActiveFilters(f: BatchFilters): boolean {
  return (
    f.range.fromMs !== null ||
    f.range.toMs !== null ||
    f.coding !== "any" ||
    f.status !== "any" ||
    f.assignment !== "any"
  );
}

/**
 * Parse a date input's `YYYY-MM-DD` into epoch ms at the LOCAL day boundary.
 *
 * `new Date("2026-08-19")` parses as UTC midnight, which in any negative-offset
 * zone is the 18th locally — so a reviewer filtering "from the 19th" would lose
 * the 19th's early batches. Constructing from parts keeps the boundary in the
 * same zone the batch dates are displayed in.
 *
 * `endOfDay` pushes to 23:59:59.999 so an inclusive "to" bound covers the whole
 * day rather than only midnight.
 */
export function dayBoundaryMs(value: string, endOfDay = false): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(date.getTime())) return null;
  if (endOfDay) date.setHours(23, 59, 59, 999);
  return date.getTime();
}

/** How many batches one page holds. FA's own coding batch list pages too; past
 *  a few hundred rows the list stops being scannable and the browser stops
 *  being responsive. */
export const BATCH_PAGE_SIZE = 100;

/** One page of batches. A page past the end is empty rather than wrapping — a
 *  wrapped page silently re-shows work the reviewer already passed. */
export function pageOf<T>(items: readonly T[], page: number, size: number): T[] {
  const start = Math.max(0, page) * size;
  return items.slice(start, start + size);
}

/** Total pages for a list, at least 1 so the footer never reads "page 1 of 0". */
export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}
